import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'

import { Source, trackAccommodationViewed } from 'analytics/map'
import ListingCard from 'components/cards/ListingCard'
import StoryCard from 'components/cards/StoryCard'
import WithSelectEscape from 'components/SelectEscape/WithSelectEscape'

const Wrapper = styled.div<{ hideTabs?: boolean }>`
  .react-tabs {
    display: flex;
    flex-direction: column;
    justify-content: center;

    &__tab-list {
      display: ${({ hideTabs }) => (hideTabs ? 'none' : 'flex')};
      align-items: center;
      list-style: none;
      background-color: ${({ theme }) => theme?.['-ds-neutral-200']?.value};
      border-radius: 100px;
      margin: 0 auto 8px auto;
      padding: 2.5px 4px;
    }

    &__tab {
      font-family: ${({ theme }) =>
        theme?.['-ds-font-size-xxxs']?.['font-family']?.value};
      font-size: ${({ theme }) =>
        theme?.['-ds-font-size-xxxs']?.['font-size']?.value};
      font-weight: 500;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      padding: 10px 20px;
      border-radius: 100px;
    }

    &__tab--selected {
      background-color: ${({ theme }) => theme?.['-ds-neutral-100']?.value};
      border: 1px solid rgba(72, 80, 93, 0.1);
      box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
    }
  }
`

interface MarkerCalloutTabbedViewProps {
  tabIndex: number
  onTabChange: (prop: any) => any
  listing: GQL_Listing
  latestStory: GQL_StoryCardFragmentFragment
  bucketListItemsIndex: GQL_BucketListItemsIndex
  showListing: boolean
  source: Source
}

const MarkerCalloutTabbedView = ({
  tabIndex,
  onTabChange,
  listing,
  latestStory,
  bucketListItemsIndex,
  showListing,
  source,
}: MarkerCalloutTabbedViewProps) => {
  const analyticsTrackedMatrix = useRef([])

  useEffect(() => {
    trackAccommodationViewed({
      listingId: listing.id,
      storyId: latestStory.id,
      source,
    })

    if (showListing) {
      // otherwise, the listing itself
      trackAccommodationViewed({
        listingId: listing.id,
        source,
      })
    }
  }, [])

  return (
    <Wrapper hideTabs={!showListing}>
      <Tabs
        defaultIndex={tabIndex}
        onSelect={(index: number) => {
          onTabChange(index)

          if (analyticsTrackedMatrix.current[index] === true) return

          trackAccommodationViewed({
            listingId: listing.id,
            storyId: latestStory.id,
            source,
          })

          analyticsTrackedMatrix.current[index] = true
        }}
      >
        <TabList>
          <Tab>Story</Tab>
          {showListing && <Tab>Listing</Tab>}
        </TabList>

        <TabPanel>
          <WithSelectEscape
            itemId={latestStory.id}
            itemType="Story"
            bucketListItemsIndex={bucketListItemsIndex}
          >
            <StoryCard
              listing={listing}
              story={latestStory}
              portrait={false}
              compactCard
            />
          </WithSelectEscape>
        </TabPanel>

        {showListing && (
          <TabPanel>
            <WithSelectEscape
              itemId={listing.id}
              itemType="Listing"
              bucketListItemsIndex={bucketListItemsIndex}
            >
              <ListingCard listing={listing} compactCard />
            </WithSelectEscape>
          </TabPanel>
        )}
      </Tabs>
    </Wrapper>
  )
}

export default MarkerCalloutTabbedView
